import {
    UnresolvedTimeframe,
    getTimeframeDescriptiveText,
    getTimeframeLabel,
    isCustomTimeframe,
    timeframeNames
} from '@squaredup/timeframes';
import Tooltip from 'components/tooltip/Tooltip';
import DashboardContext from 'contexts/DashboardContext';
import { groupBy } from 'lodash';
import { useContext, useState } from 'react';
import { CustomTimeframeForm, CustomTimeframeHeader } from './CustomTimeframeForm';
import TimeframeMenu from './TimeframeMenu';
import { isActiveTimeframe } from './timeframeFormUtils';

type ConditionalTimeframeProps =
    | {
          allowNullSelection?: false;
          onChange: (timeframe: UnresolvedTimeframe) => void;
      }
    | {
          allowNullSelection?: true;
          onChange: (timeframe: UnresolvedTimeframe | null) => void;
      };

type CommonTimeframeProps = {
    currentTimeframe?: UnresolvedTimeframe;
    disabled?: boolean;
    forceSelection?: boolean;
    forceSelectionKPI?: boolean;
    buttonClassName?: string;
    alignment?: 'end' | 'start' | 'center';
    supportedTimeframes?: boolean | string[];
    container?: HTMLElement | null;
    supportsNoneTimeframe?: boolean;
    /** If true user can customize start of year and start of week for timeframes. */
    showCalendarSettings?: boolean;
    noneTimeframeDescription?: string;
};

type TimeframeProps = ConditionalTimeframeProps & CommonTimeframeProps;

const TimeframeChooser: React.FC<TimeframeProps> = ({
    currentTimeframe,
    allowNullSelection,
    disabled = false,
    forceSelection = false,
    forceSelectionKPI = false,
    alignment = 'end',
    supportedTimeframes = true,
    container,
    supportsNoneTimeframe = false,
    showCalendarSettings = false,
    onChange
}) => {
    const { timeframe: inheritedTimeframe } = useContext(DashboardContext);
    const [isOpen, setOpen] = useState(false);
    const [customFormIsOpen, setCustomFormOpen] = useState(false);

    const groupedTimeframes = groupBy(timeframeNames, 'group');

    const timeframeLabel = currentTimeframe
        ? getTimeframeLabel(currentTimeframe)
        : `Use dashboard timeframe (${getTimeframeLabel(inheritedTimeframe)})`;

    // Always show the custom form if the current timeframe is a custom timeframe
    const showCustomForm = customFormIsOpen || isCustomTimeframe(currentTimeframe);
    // Custom timeframes only available for datastreams with full timeframe support
    const supportsCustomTimeframe = supportedTimeframes === true;
    const timeframeDescriptiveText = getTimeframeDescriptiveText(currentTimeframe || inheritedTimeframe);

    return (
        <TimeframeMenu
            open={isOpen}
            onOpenChange={() => {
                setOpen(!isOpen);
                setCustomFormOpen(false);
            }}
        >
            <TimeframeMenu.Trigger disabled={disabled} data-testid='timeframeChooser' title={timeframeDescriptiveText}>
                {timeframeLabel}
            </TimeframeMenu.Trigger>
            <TimeframeMenu.MenuContent align={alignment} container={container}>
                <TimeframeMenu.FiltersMenu>
                    {allowNullSelection && (
                        <TimeframeMenu.FilterButton
                            onClick={() => {
                                onChange(null);
                                setOpen(false);
                            }}
                            key='defaultTimeframe'
                            isActive={!currentTimeframe && !showCustomForm}
                        >
                            Use dashboard timeframe ({getTimeframeLabel(inheritedTimeframe)})
                        </TimeframeMenu.FilterButton>
                    )}

                    {(forceSelection || forceSelectionKPI) && (
                        <>
                            <TimeframeMenu.FilterButton disabled={true} key='forceSelection'>
                                <div>
                                    Use dashboard timeframe
                                    <div key='timeframeMessage' className='pt-1 text-xs text-textDisabled'>
                                        {forceSelection
                                            ? 'Unavailable: Monitoring is enabled.'
                                            : 'Unavailable: KPI is enabled.'}
                                    </div>
                                </div>
                            </TimeframeMenu.FilterButton>
                        </>
                    )}

                    {supportsNoneTimeframe && (
                        <TimeframeMenu.FilterButton
                            onClick={() => {
                                onChange('none');
                                setOpen(false);
                            }}
                            key='noTimeframe'
                            isActive={currentTimeframe === 'none' && !showCustomForm}
                        >
                            None
                        </TimeframeMenu.FilterButton>
                    )}

                    {(allowNullSelection || forceSelection || forceSelectionKPI) && <TimeframeMenu.Separator />}

                    {Object.entries(groupedTimeframes).map(([timeframeGroup, timeframes]) => {
                        return (
                            <TimeframeMenu.Group key={timeframeGroup}>
                                {timeframes.map((timeframeInstance) => {
                                    const { name: timeframe, label, getResolvedTimeRange } = timeframeInstance;
                                    const isActive = !showCustomForm && isActiveTimeframe(timeframe, currentTimeframe);

                                    const isSupportedTimeframe = Array.isArray(supportedTimeframes)
                                        ? supportedTimeframes?.includes(timeframe)
                                        : true;

                                    return (
                                        <Tooltip
                                            title='Timeframe not available for this data stream.'
                                            placement='right'
                                            disabled={isSupportedTimeframe}
                                            key={timeframe}
                                        >
                                            <TimeframeMenu.FilterButton
                                                disabled={!isSupportedTimeframe}
                                                onClick={() => {
                                                    onChange(timeframe);
                                                    setOpen(false);
                                                }}
                                                isActive={isActive}
                                            >
                                                <TimeframeMenu.TimeframeLabel data-testid={timeframe}>
                                                    {label}
                                                </TimeframeMenu.TimeframeLabel>
                                                <TimeframeMenu.TimeframePreview
                                                    data-testid={`resolvedTimeframe-${timeframe}`}
                                                >
                                                    {getResolvedTimeRange()}
                                                </TimeframeMenu.TimeframePreview>
                                            </TimeframeMenu.FilterButton>
                                        </Tooltip>
                                    );
                                })}
                            </TimeframeMenu.Group>
                        );
                    })}
                    <Tooltip
                        title='Timeframe not available for this data stream.'
                        placement='right'
                        disabled={supportsCustomTimeframe}
                    >
                        <TimeframeMenu.FilterButton
                            key='custom'
                            isActive={showCustomForm}
                            onClick={() => {
                                setCustomFormOpen(!customFormIsOpen);
                            }}
                            disabled={!supportsCustomTimeframe}
                            data-testid='customTimeframe'
                        >
                            Custom...
                        </TimeframeMenu.FilterButton>
                    </Tooltip>
                </TimeframeMenu.FiltersMenu>
                {showCustomForm && (
                    <TimeframeMenu.AdvancedMenu>
                        <CustomTimeframeHeader />
                        <CustomTimeframeForm
                            onChange={onChange}
                            currentTimeframe={currentTimeframe || inheritedTimeframe}
                            container={container}
                            showCalendarSettings={showCalendarSettings}
                        />
                    </TimeframeMenu.AdvancedMenu>
                )}
            </TimeframeMenu.MenuContent>
        </TimeframeMenu>
    );
};

export default TimeframeChooser;
